import {ApolloLink, Observable} from '@apollo/client';
import {NOT_FOUND_PREFIX} from 'src/internal/revamp/constants';
import DataNetworkError from 'src/internal/revamp/graphql/DataNetworkError';
import {extractPrefixFromVariableName} from 'src/internal/revamp/queryUtils/prefixes';
import {
  logHttpError,
  logHttpSuccess,
} from 'src/internal/revamp/telemetry/logHttp';
import loadId from 'src/internal/shared/loadId';
import {
  REQUEST_SOURCE_HEADER,
  getRequestSourceHeader,
  getRequestSourceInformation,
} from 'src/internal/shared/requestSource';
import {EXCLUDE_FROM_SENTRY} from 'src/internal/shared/telemetry/observability';

import type {FetchResult} from '@apollo/client';
import type {
  GraphQlOperation,
  InternalOperation,
} from 'src/internal/revamp/apollo/types';
import type {GqlError} from 'src/internal/revamp/graphql/GqlError';
import type {
  DataAnalyticsErrorReport,
  DataAnalyticsSuccessReport,
} from 'src/internal/revamp/telemetry/types';
import type {RequestSourceInformation} from 'src/internal/shared/requestSource';

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function getOperationInformation(
  operation: InternalOperation,
): RequestSourceInformation {
  const context = operation.getContext();

  return getRequestSourceInformation({
    service: context.service,
    project: context.queryProject,
    component: context.component,
    operation: operation.operationName,
  });
}

export default function createObservabilityLink(): ApolloLink {
  return new ApolloLink(function observabilityLink(
    operation: InternalOperation,
    forward,
  ) {
    const context = operation.getContext();
    const {headers} = context;

    const info = getOperationInformation(operation);
    const xRequestSource = getRequestSourceHeader(info);

    operation.setContext({
      headers: {
        [REQUEST_SOURCE_HEADER]: xRequestSource,
        ...headers,
      },
    });

    return new Observable(function observabilityObserver(observer) {
      const start = now();

      const subscription = forward(operation).subscribe({
        next: function observabilityLinkNext(result: FetchResult) {
          const duration = Math.round(now() - start);

          if (result.errors) {
            result.errors.forEach((error) => {
              (error as any)[EXCLUDE_FROM_SENTRY] = true;

              const gqlError = error as unknown as GqlError;
              const errorInfo: GenericErrorInfo = {
                message: gqlError.message,
                path: gqlError.path,
                actionId: gqlError.extensions.actionId,
                traceId: gqlError.extensions.traceId,
                requestId: gqlError.extensions.unstable_requestId,
                code: gqlError.extensions.error.code,
                status: gqlError.extensions.error.status,
              };
              processError(operation, duration, errorInfo);
            });
          } else {
            processSuccess(operation, duration);
          }

          return observer.next(result);
        },

        error: function observabilityLinkError(error: unknown) {
          const duration = Math.round(now() - start);

          let errorInfo: GenericErrorInfo;

          if (error instanceof DataNetworkError) {
            errorInfo = {
              message: error.message,
              path: null,
              actionId: error.extensions.actionId,
              traceId: error.extensions.traceId,
              requestId: error.extensions.unstable_requestId,
              code: error.extensions.error.code,
              status: error.extensions.error.status,
            };
          } else {
            const errorMessage: string =
              typeof error === 'object' &&
              error &&
              'message' in error &&
              typeof error.message === 'string'
                ? error.message
                : 'Unknown error';

            errorInfo = {
              message: errorMessage,
              path: null,
              actionId: null,
              traceId: null,
              requestId: null,
              code: 'could_not_get_error_code',
              status: -1,
            };
          }
          processError(operation, duration, errorInfo);

          (error as any)[EXCLUDE_FROM_SENTRY] = true;

          return observer.error(error);
        },

        complete: function observabilityLinkComplete() {
          return observer.complete();
        },
      });

      return function observabilityLinkUnsubscribe(): void {
        if (subscription) {
          subscription.unsubscribe();
        }
      };
    });
  });
}

type GenericErrorInfo = {
  message: string;
  path: ReadonlyArray<string | number> | null | undefined;
  actionId: string | null;
  traceId: string | null;
  requestId: string | null;
  code: string;
  status: number;
};

function getErrorParams(
  graphqlOperation: GraphQlOperation,
  error: GenericErrorInfo,
): {operationName: string; isExpected: boolean} {
  if (graphqlOperation.unsafeExpectAllErrors) {
    return {
      operationName: graphqlOperation.operationName,
      isExpected: true,
    };
  }

  const errorCode = error.code;

  if (
    graphqlOperation.type === 'MUTATION' ||
    graphqlOperation.type === 'STANDALONE_QUERY'
  ) {
    const operationName = graphqlOperation.operationName;
    const expectedErrors = graphqlOperation.expectedErrors;
    const isExpected = expectedErrors.includes(errorCode);

    return {operationName, isExpected};
  }

  const prefix = getPrefixFromPath(error.path);

  const operationName = graphqlOperation.operationName;
  const expectedErrors = graphqlOperation.expectedErrors;
  const isExpected = expectedErrors[prefix]?.includes(errorCode);

  return {
    operationName,
    isExpected,
  };
}

function processError(
  operation: InternalOperation,
  duration: number,
  error: GenericErrorInfo,
): void {
  const context = operation.getContext();
  const graphQlOperation = context.graphQlOperation;

  if (!graphQlOperation || !context.observabilityFns) {
    return;
  }

  const errorCode = error.code;

  const {operationName, isExpected} = getErrorParams(graphQlOperation, error);

  const reportParams: DataAnalyticsErrorReport = {
    service: context.service || '<unknown>',
    project: context.queryProject || '<unknown>',
    component: context.component || '<unknown>',
    // Back-end error code from the API error response or GraphQL error result, e.g. 'rate_limit_exceeded'
    error_code: errorCode,
    error_message: error.message,
    error_expected: isExpected,
    error_path: (error.path || []).join('.'),

    // The GraphQL operation name, e.g. PaymentsFragment
    operation_name: operationName,

    // The HTTP status code of the API response.
    network_duration: duration,
    network_status: error.status,
    network_action_id: error.actionId,
    network_request_id: error.requestId,
    network_trace_id: error.traceId,
    load_id: loadId,
  };

  logHttpError('GraphQL', context.observabilityFns, reportParams);
}

function processSuccess(operation: InternalOperation, duration: number): void {
  const context = operation.getContext();
  const graphQlOperation = context.graphQlOperation;

  if (!context.observabilityFns || !graphQlOperation) {
    return;
  }

  const observabilityFns = context.observabilityFns;

  const reportParams: DataAnalyticsSuccessReport = {
    service: context.service || '<unknown>',
    project: context.queryProject || '<unknown>',
    component: context.component || '<unknown>',
    operation_name: graphQlOperation.operationName,

    network_duration: duration,
    load_id: loadId,
  };

  logHttpSuccess('GraphQL', observabilityFns, reportParams);
}

function getPrefixFromPath(
  path: ReadonlyArray<string | number> | null | undefined,
): string {
  const firstSegment = path?.[0];
  return typeof firstSegment === 'string'
    ? extractPrefixFromVariableName(firstSegment)
    : NOT_FOUND_PREFIX;
}
